import {Injectable} from '@angular/core';
import {
  Coverage,
  DocumentName,
  MinifiedSaleReport,
  PaginatedResponse,
  PaginationParams,
  SaleReport,
} from '@emma-labs/shared/typescript';
import {mergeMap, Observable} from 'rxjs';
import {first, switchMap} from 'rxjs/operators';
import {AuthService} from '../auth.service';
import {SaleReportApiService} from './sale-report-api.service';

@Injectable({
  providedIn: 'root',
})
export class SaleReportService {
  constructor(
    private readonly api: SaleReportApiService,
    private readonly authService: AuthService,
  ) {}

  search(filter: string, excludeExternal = false, excludeOldGen = false): Observable<SaleReport[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      switchMap((session) => this.api.search(session.id, filter, excludeExternal, excludeOldGen)),
    );
  }

  getDocument(saleReportId: string, documentName: DocumentName): Observable<Blob> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      switchMap((session) => this.api.getDocument(session.id, saleReportId, documentName)),
    );
  }

  getAll(
    excludeSelfServe = false,
    excludeExternal = false,
    excludeInactiveProviders = false,
  ): Observable<MinifiedSaleReport[]> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      switchMap((session) =>
        this.api.getAll(session.id, excludeSelfServe, excludeExternal, excludeInactiveProviders),
      ),
    );
  }

  getAllPaginated(
    params: PaginationParams,
    excludeSelfServe = false,
    excludeExternal = false,
    excludeInactiveProviders = false,
  ): Observable<PaginatedResponse<MinifiedSaleReport>> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      switchMap((session) =>
        this.api.getAllPaginated(
          session.id,
          params,
          excludeSelfServe,
          excludeExternal,
          excludeInactiveProviders,
        ),
      ),
    );
  }

  sendModificationNotice(saleReportId: string): Observable<SaleReport> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.sendModificationNotice(session.id, saleReportId)),
    );
  }

  sendClientSessionAccessLink(saleReportId: string): Observable<undefined> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.sendClientSessionAccessLink(session.id, saleReportId)),
    );
  }

  updateProtection(saleReportId: string, coverage: Coverage): Observable<SaleReport> {
    return this.authService.currentAgentSessionOrAuthError.pipe(
      first(),
      mergeMap((session) => this.api.updateProtection(session.id, saleReportId, coverage)),
    );
  }
}
